import React, { useRef, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './TextEditor.css';

const TextEditor = ({ value, onChange, charLimit = 300 }) => {
  const [editorContent, setEditorContent] = useState(value || '');
  const [error, setError] = useState(false);
  const [focused, setFocused] = useState(false); // Track focus state
  const quillRef = useRef(null);

  useEffect(() => {
    setEditorContent(value || '');
  }, [value]);

  const getPlainTextLength = () => {
    if (!quillRef.current) return 0;
    const text = quillRef.current.getEditor().getText();

    // Remove only the trailing newline Quill adds
    const cleaned = text.endsWith('\n') ? text.slice(0, -1) : text;

    return cleaned.length;
  };

  const handleEditorChange = (content, delta, source, editor) => {
    const textLength = getPlainTextLength();

    if (textLength > charLimit) {
      setError(true);
      return;
    }

    setError(false);
    setEditorContent(content);
    if (onChange) {
      onChange(content);
    }
  };

  const handleBeforeInput = event => {
    if (getPlainTextLength() >= charLimit) {
      event.preventDefault();
      setError(true);
    }
  };

  const handlePaste = event => {
    if (!quillRef.current) return;

    const clipboardData = event.clipboardData.getData('text');
    const currentLength = getPlainTextLength();

    if (currentLength >= charLimit) {
      event.preventDefault();
      setError(true);
      return;
    }

    const remainingChars = charLimit - currentLength;
    const allowedText = clipboardData.substring(0, remainingChars);

    if (allowedText.length < clipboardData.length) {
      event.preventDefault();
      quillRef.current
        .getEditor()
        .clipboard.dangerouslyPasteHTML(
          quillRef.current.getEditor().getLength() - 1,
          allowedText
        );
      setError(true);
    }
  };

  const handleKeyDown = event => {
    if (getPlainTextLength() < charLimit) {
      setError(false);
    }
  };

  const modules = {
    toolbar: [['bold', 'italic', 'underline', { color: [] }]],
    history: { delay: 1000, maxStack: 100 }
  };

  return (
    <div>
      <ReactQuill
        ref={quillRef}
        value={editorContent}
        onChange={handleEditorChange}
        theme="snow"
        modules={modules}
        onFocus={() => setFocused(true)} // Set focus true
        onBlur={() => setFocused(false)} // Set focus false
        onKeyDown={handleKeyDown}
        onKeyPress={handleBeforeInput}
        onPaste={handlePaste}
      />
      {focused && (
        <p className="charLimit">
          {getPlainTextLength()}/{charLimit}
        </p>
      )}
      {error && (
        <p style={{ color: 'red', fontSize: '12px', margin: '0px' }}>
          Character limit of {charLimit} exceeded.
        </p>
      )}
    </div>
  );
};

export default TextEditor;
